/* TeamPhotosManager — Media ▸ Website ▸ Team photos (AJ 2026-10-14). Shared by AD,
 * Scheduler and Tech Specialist. Teams = the season's Roster Teams with their Org Team
 * names (no hardcoded codes). One photo per team per season; publishing is automatic:
 * the website shows each sport's ACTIVE season (current once it has a schedule in OL,
 * else last season). Doc: MEDIA_PAGE.md */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ImagePlus, Loader2, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TeamPhotosManager({ orgId, isDark }) {
  const [season, setSeason] = useState('');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const base = `${API}/api/website-media/${orgId}/team-photos`;

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(base, { params: season ? { season } : {} });
      setData(d.data);
      if (!season) setSeason(d.data.season);
    } catch (e) { setData({ rows: [], seasons: [] }); window.alert(e?.response?.data?.detail || 'Could not load team photos'); }
  }, [base, season]);
  useEffect(() => { load(); }, [load]);

  const upload = async (row, file) => {
    const fd = new FormData(); fd.append('file', file);
    setBusy(row.token);
    try { await axios.post(`${base}/${row.token}`, fd, { params: { season }, headers: { 'Content-Type': 'multipart/form-data' } }); await load(); }
    catch (e) { window.alert(e?.response?.data?.detail || 'Upload failed'); } finally { setBusy(null); }
  };
  const remove = async (row) => {
    if (!window.confirm(`Remove the ${season} photo for ${row.team_name}?`)) return;
    setBusy(row.token);
    try { await axios.delete(`${base}/${row.token}`, { params: { season } }); await load(); }
    catch (e) { window.alert(e?.response?.data?.detail || 'Remove failed'); } finally { setBusy(null); }
  };

  if (!data) return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-amber-500" /></div>;
  const muted = isDark ? 'text-slate-400' : 'text-slate-500';
  const sports = [...new Set(data.rows.map((r) => r.sport_label))];
  return (
    <div className="space-y-3" data-testid="team-photos-manager">
      <div className="flex items-center gap-2 flex-wrap">
        <select value={season} onChange={(e) => setSeason(e.target.value)} data-testid="team-photos-season"
          className={`h-8 text-sm rounded px-2 border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'}`}>
          {data.seasons.map((s) => <option key={s} value={s}>{s}{s === data.current ? ' (current)' : ''}</option>)}
        </select>
        <span className={`text-xs ${muted}`}>
          Website shows: {Object.entries(data.active_by_sport || {}).map(([sp, s]) => `${(data.rows.find((r) => r.sport === sp) || {}).sport_label || sp} ${s}`).join(' · ') || '—'}
        </span>
      </div>
      {data.rows.length === 0 && <p className={`text-sm ${muted}`}>No teams for {season} yet — add them in Org Teams ▸ Team Levels.</p>}
      {sports.map((sp) => (
        <div key={sp}>
          <div className={`text-xs font-semibold uppercase tracking-wide mb-1 ${muted}`}>{sp}</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2">
            {data.rows.filter((r) => r.sport_label === sp).map((r) => (
              <PhotoCard key={r.token} row={r} busy={busy === r.token} isDark={isDark} onFile={(f) => upload(r, f)} onRemove={() => remove(r)} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function PhotoCard({ row, busy, isDark, onFile, onRemove }) {
  const ref = useRef(null);
  const url = row.photo_url ? `${API || ''}${row.photo_url}` : '';
  return (
    <div className={`rounded border p-2 ${isDark ? 'bg-slate-900/40 border-slate-700' : 'bg-white border-slate-200'}`} data-testid={`tp-card-${row.token}`}>
      <div className={`text-xs font-semibold truncate ${isDark ? 'text-white' : 'text-slate-900'}`} title={row.team_name}>{row.team_name}</div>
      <div className="relative group mt-1 aspect-[4/3] rounded overflow-hidden bg-slate-100">
        {url ? <img src={url} alt="" className="w-full h-full object-cover" />
          : <button type="button" onClick={() => ref.current?.click()} className="w-full h-full flex flex-col items-center justify-center text-slate-400 text-[11px]">
            <ImagePlus className="h-5 w-5 mb-1" />Add photo</button>}
        {busy && <div className="absolute inset-0 bg-black/40 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-white" /></div>}
        {url && !busy && (
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center gap-2">
            <button type="button" onClick={() => ref.current?.click()} className="p-1 rounded bg-white/90 text-slate-800" title="Replace photo" data-testid={`tp-replace-${row.token}`}><ImagePlus className="h-4 w-4" /></button>
            <button type="button" onClick={onRemove} className="p-1 rounded bg-white/90 text-red-600" title="Remove photo" data-testid={`tp-remove-${row.token}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        )}
      </div>
      <div className={`mt-1 text-[10px] ${row.on_website ? 'text-emerald-600' : 'text-slate-400'}`}>
        {row.on_website ? 'On the website' : row.photo_url ? 'Not this season on the website' : 'No photo'}
      </div>
      <input ref={ref} type="file" accept="image/*" className="hidden" data-testid={`tp-file-${row.token}`}
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onFile(f); }} />
    </div>
  );
}
